import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ContactSupportOutlinedIcon from '@mui/icons-material/ContactSupportOutlined';
import { alpha, useTheme } from '@mui/material/styles';

interface NoAccessProps {
  serviceLabel?: string;
}

const SUPPORT_URL = 'https://clarivate.service-now.com/';

export default function NoAccess({ serviceLabel }: NoAccessProps) {
  const theme = useTheme();
  const ACCENT = theme.tokens.accent;
  const title = serviceLabel ? `No access to ${serviceLabel}` : 'No access';
  return (
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 4,
      }}
    >
      <Card variant="outlined" sx={{ maxWidth: 480, width: '100%' }}>
        <CardContent sx={{ p: 4 }}>
          <Stack spacing={2} alignItems="flex-start">
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: 1.5,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: alpha(ACCENT, 0.1),
                color: ACCENT,
              }}
            >
              <LockOutlinedIcon />
            </Box>
            <Typography variant="h5" sx={{ fontWeight: 700 }}>
              {title}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              You do not have permission to view this service. Contact your administrator to request
              access, or open a support ticket.
            </Typography>
            <Stack direction="row" spacing={1.5} sx={{ pt: 1 }}>
              <Button
                component={RouterLink}
                to="/"
                variant="outlined"
                size="small"
                startIcon={<ArrowBackIcon fontSize="small" />}
                sx={{ textTransform: 'none', fontWeight: 600 }}
              >
                Back to overview
              </Button>
              <Button
                component="a"
                href={SUPPORT_URL}
                target="_blank"
                rel="noopener noreferrer"
                variant="contained"
                size="small"
                startIcon={<ContactSupportOutlinedIcon fontSize="small" />}
                sx={{
                  textTransform: 'none',
                  fontWeight: 600,
                  backgroundColor: ACCENT,
                  '&:hover': { backgroundColor: theme.tokens.accentHover },
                }}
              >
                Contact support
              </Button>
            </Stack>
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
}
